import type { Project } from '@/types/project';
import type { AudioItem, TimelineItem } from '@/types/timeline';
import { measureTrimmedAudioFrames } from '@/lib/audio/trim-silence';
import { trimAudioSilence } from '@/features/timeline/stores/timeline-actions';
import { useProjectStore } from '@/features/projects/stores/project-store';
import { useItemsStore } from '@/features/timeline/stores/items-store';
import { updateProject as updateIndexedProject } from '@/lib/storage/indexeddb';
import { cenemaProjectMeta } from '@/utils/cenema-mapper';
import { getBackendFallbackUrls } from '@/utils/url-utils';
import { compactTimelineScenes } from '@/lib/audio/scene-compaction';

const BACKEND_URLS = getBackendFallbackUrls();

const CREDIT_TIMEOUT_MS = 10000;
const CREDIT_COST_PER_SEGMENT = 1;

export const VOICEOVER_SPEED_PRESETS = [0.8, 1.0, 1.1, 1.2, 1.3, 1.5, 1.8, 2.0] as const;
export const PROGRAMMATIC_VOICEOVER_SPEED_MAX = 1.0;

type VoiceoverTarget = AudioItem & {
  sceneId?: string;
  sceneText?: string;
};

type CreditResult = {
  success: boolean;
  newBalance?: number;
  error?: string;
};

type RegenerationResult = {
  regeneratedCount: number;
  trimmedCount: number;
  cost: number;
  newBalance?: number;
};

type ApplyVoiceoverSpeedResult = RegenerationResult & {
  mode: 'programmatic' | 'regenerated';
};

function compactSceneTimelineToVoiceovers(items: AudioItem[]): void {
  const itemsStore = useItemsStore.getState();
  const allItems = itemsStore.items;
  if (allItems.length === 0) return;
  const requests = items.map((item) => ({
    itemId: item.id,
    sceneId: typeof item.sceneId === 'string' ? item.sceneId : undefined,
    sceneIndex: Number.isFinite(Number((item as any).sceneIndex)) ? Number((item as any).sceneIndex) : undefined,
    targetDurationInFrames: Math.max(1, item.durationInFrames),
    leadingShiftFrames: 0,
  }));

  const compacted = compactTimelineScenes(allItems as TimelineItem[], requests);
  itemsStore.setItems(compacted.items);
}

function applyProgrammaticVoiceoverSpeed(items: VoiceoverTarget[], speed: number): number {
  const itemsStore = useItemsStore.getState();
  const allItems = itemsStore.items;
  if (allItems.length === 0 || items.length === 0) return 0;

  const sceneItems = allItems
    .filter((item) => /^Scene\s+\d+/i.test(item.label || ''))
    .sort((a, b) => a.from - b.from);

  if (sceneItems.length === 0) return 0;

  const voiceoverItems = allItems
    .filter((item): item is AudioItem & { sceneId?: string } =>
      item.type === 'audio' && (item.label || '').trim().toLowerCase() === 'voiceover'
    )
    .sort((a, b) => a.from - b.from);

  if (voiceoverItems.length === 0) return 0;

  const nextItems = allItems.map((item) => ({ ...item })) as TimelineItem[];
  const sceneWindows = sceneItems.map((sceneItem, index) => ({
    sceneItemId: sceneItem.id,
    from: sceneItem.from,
    to: index < sceneItems.length - 1 ? sceneItems[index + 1]!.from : Number.POSITIVE_INFINITY,
    duration: Math.max(1, sceneItem.durationInFrames),
  }));

  let cursor = sceneWindows[0]?.from ?? 0;
  let appliedCount = 0;

  sceneWindows.forEach((window, index) => {
    const targetVoiceover = voiceoverItems[index];
    if (!targetVoiceover) return;

    const newWindowDuration = Math.max(1, Math.round(targetVoiceover.durationInFrames / speed));
    const ratio = newWindowDuration / window.duration;
    const windowItems = nextItems.filter((item) => item.from >= window.from && item.from < window.to);

    if (windowItems.length === 0) {
      cursor += newWindowDuration;
      return;
    }

    windowItems.forEach((item) => {
      const relativeFrom = Math.max(0, item.from - window.from);
      const scaledFrom = cursor + Math.round(relativeFrom * ratio);
      const scaledDuration = Math.max(1, Math.round(item.durationInFrames * ratio));

      item.from = scaledFrom;
      item.durationInFrames = scaledDuration;

      if (item.id === window.sceneItemId) {
        item.from = cursor;
        item.durationInFrames = newWindowDuration;
      }

      if (item.id === targetVoiceover.id) {
        item.from = cursor;
        item.durationInFrames = newWindowDuration;
        item.speed = speed;
        appliedCount += 1;
      }
    });

    cursor += newWindowDuration;
  });

  const lastWindow = sceneWindows[sceneWindows.length - 1];
  if (lastWindow && Number.isFinite(lastWindow.to)) {
    const oldTimelineEnd = lastWindow.to;
    const newTimelineEnd = cursor;
    const delta = newTimelineEnd - oldTimelineEnd;
    if (delta !== 0) {
      nextItems.forEach((item) => {
        if (item.from >= oldTimelineEnd) {
          item.from += delta;
        }
      });
    }
  }

  itemsStore.setItems(nextItems);
  return appliedCount;
}

function resolveProjectUserId(project?: Pick<Project, 'input_data'> | null): string | null {
  const input = project?.input_data;
  const query = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
  return (
    input?.user_id ||
    input?.userId ||
    input?.uid ||
    input?.user?.uid ||
    query?.get('userid') ||
    query?.get('userId') ||
    null
  );
}

async function requestParentCreditDeduction(amount: number, description: string): Promise<CreditResult | null> {
  if (typeof window === 'undefined' || window.parent === window) return null;

  const requestId = crypto.randomUUID();

  return await new Promise<CreditResult | null>((resolve) => {
    const timeout = window.setTimeout(() => {
      window.removeEventListener('message', handleMessage);
      resolve(null);
    }, CREDIT_TIMEOUT_MS);

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'CREDIT_DEDUCTION_RESULT') return;
      const payload = event.data?.payload || event.data?.data;
      if (payload?.requestId !== requestId) return;

      window.clearTimeout(timeout);
      window.removeEventListener('message', handleMessage);
      resolve({
        success: Boolean(payload?.success),
        newBalance: payload?.newBalance,
        error: payload?.error,
      });
    };

    window.addEventListener('message', handleMessage);
    window.parent.postMessage(
      {
        type: 'REQUEST_CREDIT_DEDUCTION',
        payload: { requestId, amount, description },
        data: { requestId, amount, description },
      },
      '*'
    );
  });
}

async function deductVoiceoverCredits(project: Pick<Project, 'input_data'> | null | undefined, amount: number, description: string): Promise<CreditResult> {
  if (amount <= 0) return { success: true };

  const parentResult = await requestParentCreditDeduction(amount, description);
  if (parentResult) return parentResult;

  const uid = resolveProjectUserId(project);
  if (!uid) {
    return { success: false, error: '사용자 정보를 찾지 못했습니다.' };
  }

  let lastError: CreditResult = { success: false, error: '포인트 차감에 실패했습니다.' };

  for (const backendUrl of BACKEND_URLS) {
    try {
      const response = await fetch(`${backendUrl}/v1/credits/deduct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uid, amount, description }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        lastError = {
          success: false,
          error: data?.error || '포인트 차감에 실패했습니다.',
          newBalance: data?.current,
        };
        continue;
      }

      return {
        success: true,
        newBalance: data?.newBalance,
      };
    } catch (error) {
      lastError = {
        success: false,
        error: error instanceof Error ? error.message : '포인트 차감에 실패했습니다.',
      };
    }
  }

  return lastError;
}

export async function measureAudioDurationFrames(url: string, fps: number): Promise<number | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const buf = await res.arrayBuffer();
    const ctx = new (window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)();
    try {
      const decoded = await ctx.decodeAudioData(buf);
      return Math.max(1, Math.round(decoded.duration * fps));
    } finally {
      await ctx.close().catch(() => undefined);
    }
  } catch {
    return null;
  }
}

export async function persistVoiceoverSpeed(projectId: string | undefined, speed: number): Promise<void> {
  if (!projectId) return;

  const { currentProject, setCurrentProject } = useProjectStore.getState();
  const project = currentProject?.id === projectId ? currentProject : null;
  const nextInputData = {
    ...(project?.input_data || {}),
    audioConfig: {
      ...(project?.input_data?.audioConfig || {}),
      speed,
    },
  };

  await updateIndexedProject(projectId, { input_data: nextInputData });

  if (project) {
    setCurrentProject({
      ...project,
      input_data: nextInputData,
    });
  }

  cenemaProjectMeta.ttsSpeed = speed;
}

export async function regenerateVoiceoversWithSpeed({
  project,
  voiceovers,
  fps,
  speed,
  autoTrimSilence,
  updateItem,
}: {
  project?: Pick<Project, 'id' | 'input_data'> | null;
  voiceovers: VoiceoverTarget[];
  fps: number;
  speed: number;
  autoTrimSilence: boolean;
  updateItem: (id: string, patch: Partial<AudioItem>) => void;
}): Promise<RegenerationResult> {
  const targets = voiceovers.filter((item) => typeof item.sceneText === 'string' && item.sceneText.trim().length > 0);
  if (targets.length === 0) {
    return { regeneratedCount: 0, trimmedCount: 0, cost: 0 };
  }

  const cost = targets.length * CREDIT_COST_PER_SEGMENT;
  const deduction = await deductVoiceoverCredits(
    project,
    cost,
    `슈퍼에디트 보이스오버 재생성 (${targets.length}개, ${speed}x)`
  );

  if (!deduction.success) {
    throw new Error(deduction.error || '포인트가 부족합니다.');
  }

  const segments = targets.map((item) => ({
    id: item.sceneId || item.id,
    text: item.sceneText as string,
  }));

  let res: Response | null = null;
  let lastError: Error | null = null;

  for (const backendUrl of BACKEND_URLS) {
    try {
      const response = await fetch(`${backendUrl}/v1/proxy/batch/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          segments,
          voice: cenemaProjectMeta.voiceId,
          speed,
          language: 'ko-KR',
          model: 'cloud',
        }),
      });

      if (!response.ok) {
        lastError = new Error(`Batch TTS failed: ${response.status}`);
        continue;
      }

      res = response;
      break;
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Batch TTS request failed');
    }
  }

  if (!res) {
    throw lastError || new Error('Batch TTS request failed');
  }

  const data = await res.json();
  const results: { id: string; audio_url?: string }[] = (data.results || []).map((entry: any) => ({
    id: String(entry.id ?? entry.sceneIndex),
    audio_url: entry.audio_url,
  }));

  const refreshedTargets = await Promise.all(
    targets.map(async (item) => {
      const resultId = item.sceneId || item.id;
      const match = results.find((entry) => entry.id === resultId);
      if (!match?.audio_url) return null;

      const nextFrames = await measureAudioDurationFrames(match.audio_url, fps);
      const nextDuration = nextFrames || item.durationInFrames;

      updateItem(item.id, {
        src: match.audio_url,
        mediaId: match.audio_url,
        durationInFrames: nextDuration,
        sourceEnd: nextDuration,
        sourceDuration: nextDuration,
      } as Partial<AudioItem>);

      return {
        ...item,
        src: match.audio_url,
        durationInFrames: nextDuration,
      };
    })
  );

  const appliedTargets = refreshedTargets.filter((entry): entry is VoiceoverTarget => Boolean(entry));

  let trimmedCount = 0;
  if (autoTrimSilence && appliedTargets.length > 0) {
    const trimMeasurements = await Promise.all(
      appliedTargets.map(async (item) => {
        const result = await measureTrimmedAudioFrames(item.src, fps);
        if (!result) return null;
        
        // Check if there's any actual silence to trim
        if (result.leadingSilenceFrames + result.trailingSilenceFrames < 1) return null;

        return { 
          id: item.id, 
          durationInFrames: result.durationInFrames,
          leadingSilenceFrames: result.leadingSilenceFrames
        };
      })
    );

    const updates = trimMeasurements.filter(
      (entry): entry is { id: string; durationInFrames: number; leadingSilenceFrames: number } => Boolean(entry)
    );

    if (updates.length > 0) {
      trimAudioSilence(updates);
      trimmedCount = updates.length;
    }
  }

  compactSceneTimelineToVoiceovers(appliedTargets);

  await persistVoiceoverSpeed(project?.id, speed);

  return {
    regeneratedCount: appliedTargets.length,
    trimmedCount,
    cost,
    newBalance: deduction.newBalance,
  };
}

export async function applyVoiceoversWithSpeed({
  project,
  voiceovers,
  fps,
  speed,
  autoTrimSilence,
  updateItem,
}: {
  project?: Pick<Project, 'id' | 'input_data'> | null;
  voiceovers: VoiceoverTarget[];
  fps: number;
  speed: number;
  autoTrimSilence: boolean;
  updateItem: (id: string, patch: Partial<AudioItem>) => void;
}): Promise<ApplyVoiceoverSpeedResult> {
  if (speed <= PROGRAMMATIC_VOICEOVER_SPEED_MAX) {
    const appliedCount = applyProgrammaticVoiceoverSpeed(voiceovers, speed);
    await persistVoiceoverSpeed(project?.id, speed);

    return {
      mode: 'programmatic',
      regeneratedCount: appliedCount,
      trimmedCount: 0,
      cost: 0,
    };
  }

  const regenerated = await regenerateVoiceoversWithSpeed({
    project,
    voiceovers,
    fps,
    speed,
    autoTrimSilence,
    updateItem,
  });

  return {
    mode: 'regenerated',
    ...regenerated,
  };
}
